import { useApp } from '../../context/AppContext';
import { trpc } from '../../lib/trpc';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Calendar as CalendarIcon, ChevronDown, Check, Wifi, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { useEffect } from 'react';

export function TopBar() {
    const { selectedDate, setSelectedDate, selectedBranchId, setSelectedBranchId } = useApp();
    const { data: branches, isLoading } = trpc.listBranches.useQuery();
    const navigate = useNavigate();
    const location = useLocation();

    // Select first branch by default if none selected
    useEffect(() => {
        if (branches && branches.length > 0 && !selectedBranchId) {
            setSelectedBranchId(branches[0].id);
        } else if (branches && branches.length === 0 && !isLoading) {
            // If no branches, and not already on create page, redirect
            if (location.pathname !== '/create-branch') {
                navigate('/create-branch');
            }
        }
    }, [branches, selectedBranchId, setSelectedBranchId, isLoading, navigate, location.pathname]);

    const currentBranch = branches?.find(b => b.id === selectedBranchId);

    const displayBranchName = () => {
        if (isLoading) return 'Loading...';
        if (!branches || branches.length === 0) return 'Create Branch';
        return currentBranch?.name || 'Select Branch';
    };

    return (
        <div className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-14 items-center px-4 justify-between">
                {/* Branch Selector */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 pl-2 pr-1 font-semibold text-lg flex items-center gap-1">
                            {displayBranchName()}
                            <ChevronDown className="h-4 w-4 opacity-50" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        {branches?.map(branch => (
                            <DropdownMenuItem
                                key={branch.id}
                                onClick={() => setSelectedBranchId(branch.id)}
                                className="justify-between"
                            >
                                {branch.name}
                                {branch.id === selectedBranchId && <Check className="h-4 w-4 ml-2" />}
                            </DropdownMenuItem>
                        ))}
                        <DropdownMenuItem onClick={() => navigate('/create-branch')}>
                            <span className="text-primary font-medium">+ Create New Branch</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <div className="flex items-center gap-2">
                    {/* Native Date Picker */}
                    <input
                        type="date"
                        className="h-8 px-2 py-1 rounded-md border border-input bg-transparent text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        value={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
                        onChange={(e) => {
                            const date = e.target.valueAsDate;
                            if (date) setSelectedDate(date);
                        }}
                    />

                    {/* Settings Button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => navigate('/settings')}
                    >
                        <Settings className="h-4 w-4 text-muted-foreground" />
                    </Button>

                    {/* Status Icon */}
                    <div className="h-8 w-8 flex items-center justify-center">
                        <Wifi className="h-4 w-4 text-green-500" />
                    </div>
                </div>
            </div>
        </div>
    );
}
